import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { useController, useFormContext } from "react-hook-form";

import { HubHookFormInputWrapper } from "../HubHookFormInputWrapper";

export const HubHookFormDatePicker = ({
  name = "",
  defaultValue = null,
  required = false,
  rules = {},
  disabled = false,
  label = "",
  disableHelperText = true,
  helperText = "",
  monthFirst = true,
  smallLabel = true,
  hidden = false,
  includeTime = false,
  includeDate = true,
  placeholder = "MM/DD/YYYY",
  formProps = {},
  onChange = () => {},
  slots = {},
  slotProps = {},
  useSetValue = false,
  shouldUnregister = true,
  ...rest
}) => {
  const id = `HubHookField-${name}`;
  const { setValue } = useFormContext();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const {
    field: { onChange: onControllerChange, name: inputName, value, ref },
    fieldState: { error },
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue,
    shouldUnregister,
  });

  useEffect(() => {
    if (defaultValue) setValue(name, defaultValue);
  }, [defaultValue, setValue, name]);

  const errorMessage = error?.message;
  const helperTextMessage = disableHelperText
    ? null
    : errorMessage || helperText || <span>&nbsp;</span>;

  const handleChange = (value) => {
    const final = dayjs(value)?.toISOString();
    useSetValue && setValue(name, final);
    onControllerChange(final);
    onChange(final);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDownCapture = (event) => {
      const target = event.target;
      const insideInput = rootRef.current?.contains(target);
      const insidePickerPopper = target?.closest?.(".MuiPickersPopper-root");

      if (!insideInput && !insidePickerPopper) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
    };
  }, [open]);

  const inputValue = typeof value === "string" ? dayjs.utc(value).local() : null;

  const finalProps = {
    value: inputValue,
    name: inputName,
    inputRef: ref,
    onChange: handleChange,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    open,
    format: `${includeDate ? "L" : ""}${includeDate && includeTime ? " " : ""}${
      includeTime ? "LT" : ""
    }`,
    timeSteps: { minutes: 1 },
    slots: { ...slots },
    slotProps: {
      textField: {
        ...slotProps?.textField,
        inputProps: {
          ...slotProps?.textField?.inputProps,
          placeholder,
        },
        error,
      },
      ...slotProps,
    },
    disabled,
  };

  const wrapperProps = {
    hidden,
    formControlProps: formProps,
    label,
    required,
    id,
    helperText: helperTextMessage,
    errorMessage: error,
    disableHelperText,
  };

  return (
    <HubHookFormInputWrapper {...wrapperProps}>
      <div ref={rootRef}>
        {(includeTime && includeDate) || (!includeTime && !includeDate) ? (
          <DateTimePicker {...finalProps} {...rest} />
        ) : includeTime ? (
          <TimePicker {...finalProps} {...rest} />
        ) : (
          <DatePicker {...finalProps} {...rest} />
        )}
      </div>
    </HubHookFormInputWrapper>
  );
};

HubHookFormDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.any,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disableHelperText: PropTypes.bool,
  helperText: PropTypes.string,
  monthFirst: PropTypes.bool,
  smallLabel: PropTypes.bool,
  hidden: PropTypes.bool,
  includeTime: PropTypes.bool, // propTypes for the new prop
};

export default HubHookFormDatePicker;
